/**
 * License change-history API.
 * Endpoint is not yet in the generated SDK.
 */

import { client } from "@/hera-api/client/client.gen";

export interface LicenseHistoryFieldChange {
  field: string;
  old_value: string | null;
  new_value: string | null;
}

export interface LicenseHistoryEvent {
  event_id: number;
  changed_at: string;
  action: "created" | "updated" | "deleted" | string;
  table_name: string;
  table_label: string;
  record_id: string | null;
  actor_id: string | null;
  actor_name: string | null;
  actor_email: string | null;
  actor_user_type: string | null;
  changes: LicenseHistoryFieldChange[];
}

export interface LicenseHistoryResponse {
  events: LicenseHistoryEvent[];
  truncated: boolean;
}

export async function getLicenseHistory(
  licenseId: string,
): Promise<LicenseHistoryResponse> {
  const result = await client.get<LicenseHistoryResponse>({
    url: `/shared/licenses/${licenseId}/history`,
    parseAs: "json",
    security: [{ scheme: "bearer", type: "http" }],
  });

  if (result.error) {
    const err = result.error as { detail?: string; message?: string };
    throw new Error(
      err.detail ?? err.message ?? "Failed to load license history",
    );
  }

  return result.data as LicenseHistoryResponse;
}

type LicenseHistoryActor = Pick<
  LicenseHistoryEvent,
  "actor_name" | "actor_email"
>;

export function licenseHistoryActorLabel(event: LicenseHistoryActor): string {
  if (event.actor_name && event.actor_email) {
    return `${event.actor_name} (${event.actor_email})`;
  }
  if (event.actor_name) return event.actor_name;
  if (event.actor_email) return event.actor_email;
  return "System";
}

/** Actor label without the email, for compact surfaces such as the history card. */
export function licenseHistoryActorShortLabel(
  event: LicenseHistoryActor,
): string {
  return event.actor_name?.trim() || event.actor_email?.trim() || "System";
}

/** One status transition on the license row itself. */
export interface LicenseStatusChange {
  eventId: number;
  changedAt: string;
  /** True for the status the license was created with. */
  isInitial: boolean;
  oldStatus: string | null;
  newStatus: string | null;
  actor: string;
  actorUserType: string | null;
}

/**
 * Status transitions from license audit events, newest first.
 *
 * Cron and other unattended writes have no actor, so they surface as "System".
 * Related tables (payments, comments, inspections) are ignored; only the
 * license row carries registration status.
 */
export function licenseStatusChangesFromEvents(
  events: LicenseHistoryEvent[],
): LicenseStatusChange[] {
  const changes: LicenseStatusChange[] = [];
  for (const event of events) {
    if (event.table_name !== "license") continue;
    if (event.action === "deleted") continue;
    const statusChange = event.changes.find(
      (change) => change.field === "status",
    );
    if (!statusChange) continue;
    const isInitial = event.action === "created";
    if (!isInitial && statusChange.new_value === statusChange.old_value) {
      continue;
    }
    changes.push({
      eventId: event.event_id,
      changedAt: event.changed_at,
      isInitial,
      oldStatus: statusChange.old_value,
      newStatus: statusChange.new_value,
      actor: licenseHistoryActorShortLabel(event),
      actorUserType: event.actor_user_type,
    });
  }
  return changes;
}

export function humanizeLicenseHistoryField(field: string): string {
  return field.replaceAll("_", " ");
}
